import Chip from '@mui/material/Chip';
import { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
    chip: {
        height: 20,
        fontWeight: 600,
        fontSize: '0.7rem',
        '& .MuiChip-label': {
            padding: '0 8px',
        },
    },
    empty: {
        color: theme.palette.text.disabled,
        fontSize: '0.8125rem',
    },
}));

interface LazyCountChipProps {
    /** Hook that returns { count, isLoading, isError } when enabled. */
    useCount: (enabled: boolean) => {
        count: number | undefined;
        isLoading: boolean;
        isError: boolean;
    };
}

/**
 * Generic count chip that defers its data-fetch until the element scrolls
 * into view (IntersectionObserver with 200px rootMargin). Renders a dash
 * placeholder while loading and a Chip with the count once ready.
 */
export const LazyCountChip = ({ useCount }: LazyCountChipProps) => {
    const { classes } = useStyles();
    const ref = useRef<HTMLSpanElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '200px' },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const { count, isLoading, isError } = useCount(isVisible);

    if (!isVisible || isLoading || isError || count === undefined) {
        return (
            <span ref={ref} className={classes.empty}>
                —
            </span>
        );
    }

    return <Chip size="small" label={count} color="default" className={classes.chip} />;
};
